import * as OlProj from 'ol/proj';
import OlStyle from 'ol/style/Style';
import OlCircle from 'ol/style/Circle';
import OlFill from 'ol/style/Fill';
import OlStroke from 'ol/style/Stroke';
import OlText from 'ol/style/Text.js';
import OlFeature from 'ol/Feature';
import OlPoint from 'ol/geom/Point';
import OlRegularShape from 'ol/style/RegularShape.js';
import OlStyleIcon from 'ol/style/Icon';

import OlView from 'ol/View';
import { CDevice, CView, READING_TYPE_MAP } from '@ts/ui-models';
import { CMapElement } from '@models/maps/Map';
import { CElement } from '@ts/ui-models';
import { CLocation } from '@ts/ui-models';
import { CLabel } from '@ts/ui-models';
import { CStatusIndicator } from '@ts/ui-models';
import { CUnitFormatter } from '@ts/ui-models';

//@ts-ignore
import otherPoint from '../Map/location.svg';
import { CGeometry } from '@ts/ui-models';
import LineString from 'ol/geom/LineString';
import { IMapOptions } from '@interfaces/maps/Map.interface';
import { CReading } from '@ts/ui-models';

export const DEFAULT_ICON = {
  color: '#0693e3',
  name: 'location_on',
};

export const createGeoJsonObject = (geometriesArray: CGeometry[], projectionCode: 'EPSG:3857' | 'EPSG:4326') => {
  let geoJsonProperty;
  switch (projectionCode) {
    case 'EPSG:3857':
      geoJsonProperty = 'geomLocalRectCoord';
      break;
    case 'EPSG:4326':
      geoJsonProperty = 'geomGpsCoord';
      break;
    default:
  }
  const features = geometriesArray.map(geom => {
    return {
      type: 'Feature',
      geometry: geom[geoJsonProperty],
      properties: {
        entityID: geom.getID(),
      },
    };
  });

  return {
    type: 'FeatureCollection',
    crs: {
      type: 'name',
      properties: {
        name: projectionCode,
      },
    },
    features,
  };
};

export const createLocJsonObject = (locationArray: CLocation[], projectionCode: 'EPSG:3857' | 'EPSG:4326') => {
  let geoJsonProperty;
  switch (projectionCode) {
    case 'EPSG:3857':
      geoJsonProperty = 'geomLocalRectCoord';
      break;
    case 'EPSG:4326':
      geoJsonProperty = 'geomGpsCoord';
      break;
    default:
  }
  const features = locationArray.map(loc => {
    return {
      type: 'Feature',
      geometry: loc.getGeometry()[geoJsonProperty],
      properties: {
        entityID: loc.getID(),
      },
    };
  });

  return {
    type: 'FeatureCollection',
    crs: {
      type: 'name',
      properties: {
        name: projectionCode,
      },
    },
    features,
  };
};

export const updateMapElement = (
  element: CElement,
  unitFormatter: CUnitFormatter,
  tMapElement?: CMapElement,
  readingType?: READING_TYPE_MAP
): CMapElement => {

  const tElement: any = {
    id: element.id,
    customId: element.customId,
    name: element.name,
    elementType: element.elementType,
  };
  console.log('tElement', tElement);
  console.log('tMapElement', tMapElement);
  const newCMapElement = tMapElement ?? new CMapElement(tElement, unitFormatter);
  if (newCMapElement.needUpdate) {
    newCMapElement.locations = element.xlocs?.map(xl => new CLocation(xl.location)) ?? [];
    newCMapElement.labels = element.xItemTags?.filter(xi => xi && typeof xi === 'object').map(xi => new CLabel(xi.tag)) ?? [];
    newCMapElement.statuses = element.currentStatus?.indicators.map(xi => new CStatusIndicator(xi, unitFormatter)) ?? [];
    newCMapElement.defaultIcon = element.getIcon();
    newCMapElement.attachedDevices = element.attachedDevices?.map(at => new CDevice(at));
    newCMapElement.needUpdate = false;
    newCMapElement.notify('remarkFeature');

    if (element?.getLastPosition(readingType)) {
      newCMapElement.lastPosition = element.getLastPosition(readingType);
    }
  }
  return newCMapElement;
};

/**
 * Generates a map view based on the provided OpenLayers library and view configuration.
 *
 * @param {IView} view - The view configuration object.
 * @return {{ view: OlView, projection: OlProj.Projection }} An object containing the generated map view and projection.
 */
const generateOpenLayerMapView = (view: CView) => {
  const projectionCode = view.getProjectionCode();

  const newOlView = (projection?: any) =>
    new OlView({
      projection,
      center: view.getCenterView(),
      zoom: view.getZoom(),
      minZoom: view.getMinZoom(),
      maxZoom: view.getMaxZoom(),
    });

  switch (projectionCode) {
    case 'EPSG:3857':
      const projection3867 = new OlProj.Projection({
        code: projectionCode,
        units: 'm',
        extent: computeExtent(view.getPropertiesBackground()),
      });

      const view3857 = newOlView(projection3867);
      return { view: view3857, projection: projection3867 };

    case 'EPSG:4326':
    default:
      const projection4326 = new OlProj.Projection({
        code: projectionCode,
        units: 'm',
        extent: OlProj.get('EPSG:4326')?.getExtent() || [0, 0, 0, 0],
      });

      const view4326 = newOlView();

      return { view: view4326, projection: projection4326 };
  }
};

const getIndexLayers = (type: 'cluster' | 'items' | 'locations' | 'imageLayer') => {
  switch (type) {
    case 'cluster':
      return 5;
    case 'items':
      return 3;
    case 'locations':
      return 2;
    case 'imageLayer':
      return 1;
    default:
      return 0;
  }
};

//#region Extent
/**
 * Computes the extent of a layer background based on its properties.
 * @param {ILayerProperties | null} layerProperties - The properties of the layer.
 * @returns {number[]} The extent of the layer background.
 */
const computeExtent = (layerProperties: IMapOptions | null) => {
  const { layerCenterCoord, layerSize } = layerProperties ?? {};

  if (!layerCenterCoord || !layerSize) return [];
  const extent = [
    -layerCenterCoord[0],
    -layerCenterCoord[1],
    -layerCenterCoord[0] + layerSize[0],
    -layerCenterCoord[1] + layerSize[1],
  ];
  return extent;
};

/**
 * Checks if two extents intersect.
 *
 * @param {Array} extent1 - An array representing the first extent.
 * @param {Array} extent2 - An array representing the second extent.
 * @return {boolean} Returns true if the extents intersect, false otherwise.
 */
const isExtentIntersects = (extent1: Array<number>, extent2: Array<number>) => {
  const [xmin1, ymin1, xmax1, ymax1] = extent1;
  const [xmin2, ymin2, xmax2, ymax2] = extent2;

  const intersects = xmin1 <= xmax2 && xmax1 >= xmin2 && ymin1 <= ymax2 && ymax1 >= ymin2;

  return intersects;
};
//#endregion

//#region Styling
/**
 * Generates an OlStyle object for the stroke of a line associated with an item.
 *
 * @param {type} itemID - the logical ID of the item
 * @return {OlStyle} the OlStyle object for the stroke of the line
 */
const generateStyleForLine = (color: string): OlStyle => {
  return new OlStyle({
    stroke: new OlStroke({
      color: color,
      width: 3,
    }),
  });
};
/**
 * Generates an OlStyle object for the inner point of an item.
 *
 * @param {type} itemID - the logical ID of the item
 * @return {OlStyle} the OlStyle object for the inner point
 */
const generateStyleInnerPoint = (color: string): OlStyle => {
  return new OlStyle({
    image: new OlCircle({
      fill: new OlFill({
        color: color,
      }),
      radius: 5,
    }),
  });
};

//#region WORM
/**
 * Calculates the angle in degrees between two coordinates.
 * @param {number[]} coord1 The first coordinate.
 * @param {number[]} coord2 The second coordinate.
 * @returns {number} The angle in degrees.
 */
const calculateAngleInDegrees = (coord1: [number, number], coord2: [number, number]): number => {
  const deltaX = coord2[0] - coord1[0]; // Diferencia en X (longitud)
  const deltaY = coord2[1] - coord1[1]; // Diferencia en Y (latitud)

  // Calcular el ángulo en radianes usando atan2, que maneja la dirección correctamente
  const angleInRadians = Math.atan2(deltaY, deltaX);

  // Convertir el ángulo de radianes a grados
  let angleInDegrees = (angleInRadians * 180) / Math.PI;

  // Asegurarse de que el ángulo esté en el rango [0, 360] grados
  if (angleInDegrees < 0) {
    angleInDegrees += 360;
  }

  return angleInDegrees; // Retornar el ángulo en grados
};

// Función para crear el estilo de la flecha/triángulo
const createDirectionMarkerStyle = (color: string, degree: number): OlStyle => {
  return new OlStyle({
    image: new OlRegularShape({
      fill: new OlFill({ color: color }), // Color basado en el itemID
      points: 3, // Triángulo
      radius: 10, // Tamaño del triángulo
      rotation: -((degree * Math.PI) / 180), // Convertimos grados a radianes para la rotación
      angle: Math.PI / 2, // Ajuste para alinear el triángulo correctamente (punta hacia norte)
    }),
  });
};

/**
 * Generates an array of features representing the "worm" effect, i.e. an array of points with a
 * direction arrow at each midpoint between the points. The arrow points from the older point to the
 * newer point.
 * @param {string} itemID The ID of the item to generate points for.
 * @param {number} count The starting number for the IDs of the generated points.
 * @param {string} color The color of the points.
 * @param {OlFeature<OlPoint>[]} features The array of points to generate the worm effect for.
 * @returns {OlFeature<OlPoint>[]} An array of features representing the worm effect.
 */
const generateIntermediedPointAndDirectionalMarkersWorm = (
  itemID: string,
  color: string,
  features: OlFeature<OlPoint>[],
  count: number
) => {
  const readingsId: string[] = features.map(feature => feature.get('reading').id);
  const coordinates: [number, number][] = features.map(feature => feature.getGeometry()?.getCoordinates() as [number, number]);
  const intermediedPoints: OlFeature<OlPoint>[] = [];

  // Iterate backward from the second-last point to the first point
  for (let i = coordinates.length - 1; i > 0; i--) {
    const currentCoord = coordinates[i]; // Current point (older)
    const nextCoord = coordinates[i - 1]; // Next point (newer)

    // Continue only if coordinates are different
    if (currentCoord[0] !== nextCoord[0] || currentCoord[1] !== nextCoord[1]) {
      // Calculate the midpoint between currentCoord and nextCoord
      const midPoint: [number, number] = [(currentCoord[0] + nextCoord[0]) / 2, (currentCoord[1] + nextCoord[1]) / 2];

      // Calculate the rotation angle for direction
      const degree = calculateAngleInDegrees(currentCoord, nextCoord);

      // Create a new feature for the midpoint and apply directional style
      const midPointFeature = new OlFeature(new OlPoint(midPoint));
      midPointFeature.setId(`${itemID}-mid-point-${readingsId[i]}`);
      midPointFeature.set('noTooltip', true);
      midPointFeature.setStyle(createDirectionMarkerStyle(color, degree)); // Apply the arrow style with the calculated angle
      intermediedPoints.push(midPointFeature); // Add midpoint feature to the array
    }
    // Set style on the current point
    const feature = features[i];
    feature.setId(`${itemID}-point-${readingsId[i]}`);
    feature.setStyle(generateStyleInnerPoint(color)); // Apply the inner point style
    intermediedPoints.push(feature); // Add the original point to the array
  }

  return intermediedPoints;
};

const generateLinesWorm = (itemID: string, transformedReadings: CReading[], color: string, count: number) => {
  const itemReadingsLine: OlFeature<LineString>[] = [];
  for (let i = 0; i < transformedReadings.length - 1; i++) {
    const currentReading = transformedReadings[i];
    const oldReading = transformedReadings[i + 1];
    if (currentReading.coords && oldReading.coords) {
      // Create a new line feature between currentReading and oldReading
      const line = new OlFeature({
        geometry: new LineString([currentReading.coords, oldReading.coords]),
        itemID,
      });

      // Apply the style based on the item's associated color
      line.setStyle(generateStyleForLine(color));
      line.setId(`${itemID}-line-${oldReading.id}`);

      // Add the line to the array
      itemReadingsLine.push(line);
    }
  }

  return itemReadingsLine;
};

//#endregion

//#region GeometriesLayer
const paintGeometryLayer = (feature, type = 0, zoom = 2): OlStyle => {
  const tLocation: CLocation = feature.get('tLocation');

  let newStyle: any;
  if (feature instanceof OlPoint) {
    newStyle = defaultPointStyle(tLocation.getName());
  } else {
    newStyle = defaultPolygonStyle(tLocation.getName());
  }

  return newStyle;
};

const defaultPolygonStyle = (locationName: string) => {
  //type=1=otherGeomtery

  return new OlStyle({
    fill: new OlFill({
      color: '#077bc0' + '33',
    }),
    stroke: new OlStroke({
      color: '#201b50', // theme.palette.primary.main
      width: 1,
    }),
    ...(locationName && { text: geometryLabel('Polygon', locationName) }),
  });
};

const defaultPointStyle = (nameLocation: string, zoom = 2) => {
  const getIconScaleForZoom = zoom => {
    return 0.1 + zoom * 0.02;
  };
  const scale = getIconScaleForZoom(zoom);

  //type=1=otherGeomtery
  const iconStyle = new OlStyle({
    image: new OlStyleIcon({
      anchor: [0.5, 1],
      anchorXUnits: 'fraction',
      anchorYUnits: 'fraction',
      src: otherPoint,
      scale: scale,
      // rotation: Math.PI / 4,
    }),
    ...(nameLocation && { text: geometryLabel('Point', nameLocation) }),
  });

  return iconStyle;
};

const geometryLabel = (type: 'Polygon' | 'Point', name: string) => {
  return new OlText({
    font: '13px Roboto',
    text: name,
    fill: new OlFill({
      color: '#ffffff',
    }),
    backgroundFill: new OlFill({
      color: 'rgba(0, 0, 0, 0.5)',
    }),
    padding: [3, 2, 1, 5],

    textBaseline: 'middle',
    offsetY: type === 'Polygon' ? 0 : 10,
    textAlign: 'center',
  });
};

//#endregion
//#endregion

const VIEW_ERRORS = {
  NO_LAYER_ATTACHED: 'View has no layer attached',
  NO_LAYER_SIZE: 'View layer requires width and height',
  NO_MAP_SIZE: '2d views require layer width and height',
  NO_RULESET: 'At least 1 view layer must have a ruleset',
};
const checkViewErrors = (view?: CView | null) => {
  if (!view) return false;
  if (!Object.keys(view).length) return false;
  const { xViewLayers, viewTypeId } = view;

  if (!xViewLayers || !xViewLayers.length) return VIEW_ERRORS.NO_LAYER_ATTACHED;
  const cleanxViewLayers = xViewLayers.filter(xViewLayer => !!xViewLayer.layer);
  if (
    viewTypeId === '2d' &&
    cleanxViewLayers &&
    cleanxViewLayers.some(
      xViewLayer =>
        !xViewLayer.properties.layerSize ||
        (xViewLayer.properties.layerSize.some(axisComponent => axisComponent === null) && xViewLayer.layer?.background)
    )
  )
    return VIEW_ERRORS.NO_LAYER_SIZE;

  if (
    cleanxViewLayers.some(xViewLayer => !xViewLayer.layer?.xLayerRulesets && !xViewLayer.layer?.background) ||
    cleanxViewLayers.some(xViewLayer => xViewLayer.layer?.xLayerRulesets.length === 0 && !xViewLayer.layer?.background)
  )
    return VIEW_ERRORS.NO_RULESET;
  return false;
};

export {
  generateOpenLayerMapView,
  getIndexLayers,
  //Projection
  computeExtent,
  isExtentIntersects,

  //Styling
  generateStyleForLine,
  generateStyleInnerPoint,
  generateIntermediedPointAndDirectionalMarkersWorm,
  generateLinesWorm,
  paintGeometryLayer,

  //Errors
  checkViewErrors,
};
